import {
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnInit,
  ViewChild,
  AfterViewInit,
  Inject,
  PLATFORM_ID
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { environment } from '@environments/environment';

@Component({
  selector: 'app-captcha',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './captcha.html',
  styleUrls: ['./captcha.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => Captcha),
      multi: true
    }
  ]
})
export class Captcha implements ControlValueAccessor, OnInit, AfterViewInit {
  @ViewChild('divRecaptcha', { static: true })
  divRecaptcha!: ElementRef<HTMLDivElement>;

  @Input() size: 'normal' | 'compact' | 'invisible' = 'normal';

  reCaptchaResponse: string = '';
  private widgetId: number | null = null;
  private isBrowser: boolean;

  onChange: (value: string) => void = () => {};
  onTouched: () => void = () => {};

  constructor(@Inject(PLATFORM_ID) private platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    if (this.isBrowser) {
      this.loadRecaptchaScript();
    }
  }

  private loadRecaptchaScript(): void {
    if (!(this.isBrowser && window)) return;

    const w = window as any;
    if (w.grecaptcha) {
      this.renderCaptcha();
      return;
    }

    const existingScript = document.querySelector<HTMLScriptElement>(
      'script[src*="recaptcha"]'
    );
    if (existingScript) {
      existingScript.addEventListener('load', () => this.renderCaptcha());
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = () => this.renderCaptcha();
    document.body.appendChild(script);
  }

  private renderCaptcha(): void {
    if (!this.isBrowser) return;

    const grecaptcha = (window as any).grecaptcha;

    if (!grecaptcha || !this.divRecaptcha) {
      setTimeout(() => this.renderCaptcha(), 500);
      return;
    }

    if (this.widgetId !== null) return;

    const options: Record<string, unknown> = {
      sitekey: environment.captcha_siteKey,
      callback: (response: string) => {
        this.reCaptchaResponse = response;
        this.onChange(this.reCaptchaResponse);
        this.onTouched();
      },
      'expired-callback': () => this.resetCaptcha()
    };

    if (this.size === 'invisible') {
      options['size'] = 'invisible';
    } else if (this.size === 'compact') {
      options['size'] = 'compact';
    }

    this.widgetId = grecaptcha.render(this.divRecaptcha.nativeElement, options);
  }

  writeValue(value: string): void {
    this.reCaptchaResponse = value || '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  execute(): void {
    if (!this.isBrowser) return;

    const grecaptcha = (window as any).grecaptcha;
    if (!grecaptcha) return;

    if (this.widgetId === null) {
      this.renderCaptcha();
      setTimeout(() => this.execute(), 100);
      return;
    }

    grecaptcha.execute(this.widgetId);
  }

  resetCaptcha(): void {
    if (!this.isBrowser) return;

    const grecaptcha = (window as any).grecaptcha;
    if (this.widgetId !== null && grecaptcha) {
      grecaptcha.reset(this.widgetId);
      this.reCaptchaResponse = '';
      this.onChange(this.reCaptchaResponse);
    }
  }
}
